// 导入文件上传工具
import { uploadFile } from './uploadUtils';
import { sanitizeUserFacingHttpErrorMessage } from './httpErrorMessage';
import {
  authenticatedFetch,
  refreshAccessToken,
  SESSION_EXPIRED_MESSAGE,
  SESSION_BUSY_MESSAGE,
} from './authenticatedFetch';

export { refreshAccessToken, SESSION_EXPIRED_MESSAGE, SESSION_BUSY_MESSAGE };

// API客户端配置
// 使用相对路径，通过Next.js rewrite规则代理到后端
// 在客户端使用 /api，Next.js会自动代理到后端服务器
const API_BASE_URL = typeof window !== 'undefined' 
  ? ''  // 客户端：使用相对路径，通过Next.js rewrite
  : (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002');  // 服务端：直接访问后端

// 请求拦截器
const request = async (endpoint: string, options: RequestInit = {}) => {
  // 如果是客户端，使用相对路径 /api，通过Next.js rewrite代理
  // 如果是服务端，直接使用完整URL
  const basePath = typeof window !== 'undefined' ? '/api' : `${API_BASE_URL}/api`;
  const url = `${basePath}${endpoint}`;
  
  // 默认headers
  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  };

  // 智能设置Content-Type
  if (options.body instanceof FormData) {
    // FormData请求：让浏览器自动设置Content-Type（包含boundary）
    // 不设置Content-Type，让浏览器自动处理
  } else if (options.headers && 'Content-Type' in options.headers) {
    // 如果用户明确指定了Content-Type，使用用户指定的
    // 不设置默认Content-Type
  } else {
    // 默认情况：设置为application/json（适用于大多数API）
    headers['Content-Type'] = 'application/json';
  }

  // 合并自定义headers（允许覆盖Content-Type）
  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  // 添加认证token
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const config: RequestInit = {
    ...options,
    headers,
  };

  try {
    const allowAuthRetry = endpoint !== '/auth/refresh';
    const response = await authenticatedFetch(url, config, { allowAuthRetry });
    
    if (!response.ok) {
      if (response.status === 401) {
        // authenticatedFetch 已尝试过 refresh；此处仅处理最终 401
        let errorData: any = null;
        let rawBody: string | null = null;

        try {
          // 只读取一次body，避免"body stream already read"错误
          rawBody = await response.text(); 

          // 尝试将原始文本解析为JSON
          try {
            errorData = JSON.parse(rawBody);
          } catch (parseError) {
            // 如果JSON解析失败，则使用原始文本作为消息
            errorData = { message: rawBody };
          }
        } catch (readError) {
          // 如果连读取文本体都失败了
          errorData = { message: '请求失败: 无法读取响应体' };
        }

        // 初始会话检查 /auth/me 的 401（无有效 token）为预期情况，不打印错误日志
        const isInitialAuthCheck = endpoint === '/auth/me';
        if (!isInitialAuthCheck) {
          console.error('[API] ❌ 认证失败:', {
            url,
            method: options.method || 'GET',
            status: response.status,
            statusText: response.statusText,
            errorData,
            rawBody: rawBody ? rawBody.substring(0, 1000) : null,
          });
        }

        const rawMsg = errorData?.error || errorData?.message || SESSION_EXPIRED_MESSAGE;
        const err = new Error(
          sanitizeUserFacingHttpErrorMessage(response.status, typeof rawMsg === 'string' ? rawMsg : String(rawMsg))
        );
        (err as any).data = errorData;
        throw err;
      }

      // 对于非401错误，读取错误信息
      let errorData: any = null;
      let rawBody: string | null = null;

      try {
        // 只读取一次body，避免"body stream already read"错误
        rawBody = await response.text(); 

        // 尝试将原始文本解析为JSON
        try {
          errorData = JSON.parse(rawBody);
        } catch (parseError) {
          // 如果JSON解析失败，则使用原始文本作为消息
          errorData = { message: rawBody };
        }
      } catch (readError) {
        // 如果连读取文本体都失败了
        errorData = { message: '请求失败: 无法读取响应体' };
      }

      // 详细的错误日志（用于调试）
      console.error('[API] ❌ 请求失败:', {
        url,
        method: options.method || 'GET',
        status: response.status,
        statusText: response.statusText,
        errorData,
        rawBody: rawBody ? rawBody.substring(0, 1000) : null,
        headers: Object.fromEntries(response.headers.entries())
      });

      // 根据状态码和解析到的错误数据构建并抛出错误
      if (response.status === 400) {
        const rawMsg = errorData?.error || errorData?.message || '数据验证失败';
        const errorMessage = sanitizeUserFacingHttpErrorMessage(
          response.status,
          typeof rawMsg === 'string' ? rawMsg : String(rawMsg)
        );
        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).data = errorData;
        throw error;
      }
      if (response.status >= 500 && errorData?.details) {
        // 5xx 时优先展示后端返回的 details，便于排查（如连接失败、配置错误等）
        const rawMsg = errorData.details || errorData?.error || errorData?.message || `请求失败: ${response.status}`;
        const errorMessage = sanitizeUserFacingHttpErrorMessage(
          response.status,
          typeof rawMsg === 'string' ? rawMsg : String(rawMsg)
        );
        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).data = errorData;
        throw error;
      }
      const statusText = response.statusText || `HTTP ${response.status}`;
      const rawFallback = errorData?.error || errorData?.message || `请求失败: ${statusText}`;
      const errorMessage = sanitizeUserFacingHttpErrorMessage(
        response.status,
        typeof rawFallback === 'string' ? rawFallback : String(rawFallback)
      );
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).data = errorData;
      throw error;
    }

    // 解析成功响应
    let data;
    try {
      const responseText = await response.text();
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[API] ❌ JSON解析失败:', {
          url,
          error: parseError,
          responseText: responseText.substring(0, 500)
        });
        throw new Error('响应格式错误: 无法解析JSON');
      }
    } catch (jsonError) {
      // JSON解析失败，尝试读取原始响应
      const text = await response.clone().text().catch(() => '无法读取响应体');
      console.error('[API] JSON解析失败:', {
        status: response.status,
        statusText: response.statusText,
        url: url,
        rawBody: text.substring(0, 200),
        headers: Object.fromEntries(response.headers.entries())
      });
      throw new Error('响应格式错误，无法解析JSON');
    }
    return data;
  } catch (error) {
    // 网络错误处理
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('网络连接失败，请检查您的网络连接');
    }
    
    // 重新抛出已知错误
    if (error instanceof Error) {
      throw error;
    }
    
    // 未知错误
    throw new Error('API请求失败，请稍后重试');
  }
};

// API方法
export const api = {
  // 活跃心跳（在线感知）
  presence: {
    heartbeat: async (channel: 'web' | 'wechat' = 'web') => {
      return request('/presence/heartbeat', {
        method: 'POST',
        body: JSON.stringify({ channel })
      });
    }
  },

  // 认证相关
  auth: {
    // 用户注册
    register: async (userData: {
      email: string;
      password: string;
      role?: string;
      agreed_to_terms?: boolean;
      agreed_to_privacy?: boolean;
      invite_token?: string;
    }) => {
      return request('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    },
    
    // 获取公开协议（服务条款/隐私政策/AI创作指南，无需认证）
    getPublicAgreement: async (agreementType: 'terms_of_service' | 'privacy_policy' | 'ai_creation_guidelines') => {
      // 使用公开请求函数，不添加 token
      const basePath = typeof window !== 'undefined' ? '/api' : (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002') + '/api';
      const url = `${basePath}/auth/public-agreement?agreement_type=${agreementType}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    },

    // 用户登录
    login: async (credentials: { email: string; password: string }) => {
      return request('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
    },

    // 用户登出
    logout: async () => {
      return request('/auth/logout', {
        method: 'POST',
      });
    },

    // 获取当前用户信息
    getMe: async () => {
      return request('/auth/me');
    },

    // 验证令牌
    verifyToken: async (token: string) => {
      return request('/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
    },

    // 刷新令牌
    refreshToken: async (refreshToken: string) => {
      return request('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    },

    // 验证邮箱
    verifyEmail: async (token: string) => {
      return request('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
    },

    // 重新发送验证邮件
    resendVerification: async (email: string) => {
      return request('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },

    // 重置密码
    resetPassword: async (email: string) => {
      return request('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },

    // 确认重置密码
    confirmResetPassword: async (token: string, newPassword: string) => {
      return request('/auth/confirm-reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      });
    },
  },

  invites: {
    preview: async (token: string) => {
      const basePath = typeof window !== 'undefined' ? '/api' : (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002') + '/api';
      const url = `${basePath}/invites/preview?token=${encodeURIComponent(token)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      return response.json();
    },

    accept: async (inviteToken?: string | null) => {
      return request('/invites/accept', {
        method: 'POST',
        body: JSON.stringify({ invite_token: inviteToken || undefined }),
      });
    },
  },

  albums: {
    updateCreatorEmail: async (
      albumId: string,
      payload: { creator_id?: string; creator_name: string; email: string | null }
    ) => {
      return request(`/albums/${albumId}/creator-email`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },
  },

  // 用户相关
  users: {
    // 获取用户信息
    getProfile: async () => {
      return request('/users/profile');
    },

    // 更新用户信息
    updateProfile: async (profileData: any) => {
      return request('/users/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
    },

    // 更改密码
    changePassword: async (passwordData: { currentPassword: string; newPassword: string }) => {
      return request('/users/change-password', {
        method: 'PUT',
        body: JSON.stringify(passwordData),
      });
    },

    // 更改邮箱
    changeEmail: async (newEmail: string) => {
      return request('/users/change-email', {
        method: 'PUT',
        body: JSON.stringify({ newEmail }),
      });
    },

    // 删除账户
    deleteAccount: async () => {
      return request('/users/account', {
        method: 'DELETE',
      });
    },

    // 获取通知偏好设置
    getNotificationPreferences: async () => {
      return request('/users/notification-preferences');
    },

    // 更新通知偏好设置
    updateNotificationPreferences: async (preferences: {
      email_enabled?: boolean;
      wechat_contact_enabled?: boolean;
      work_notifications?: boolean;
      account_notifications?: boolean;
      revenue_notifications?: boolean;
      system_notifications?: boolean;
    }) => {
      return request('/users/notification-preferences', {
        method: 'PUT',
        body: JSON.stringify(preferences),
      });
    },

    getUiPreferences: async () => {
      return request('/users/ui-preferences');
    },

    updateDashboardBrokerPromoDismiss: async (body: {
      dismissType: 'snooze' | 'permanent';
      campaignId?: string;
    }) => {
      return request('/users/ui-preferences/dashboard-broker-promo', {
        method: 'PUT',
        body: JSON.stringify(body)
      });
    },

    updateGettingStartedPrefs: async (body: {
      ackData?: boolean;
      ackRevenue?: boolean;
      celebrated?: boolean;
    }) => {
      return request('/users/ui-preferences/getting-started', {
        method: 'PUT',
        body: JSON.stringify(body)
      });
    }
  },

  // 创作者相关
  creators: {
    // 获取音乐人档案
    getProfile: async () => {
      return request('/creators/profile');
    },

    // 保存音乐人档案（草稿）
    saveProfile: async (profileData: any) => {
      return request('/creators/profile', {
        method: 'POST',
        body: JSON.stringify(profileData),
      });
    },

    // 更新商务合作与联系方式
    updateBusinessContact: async (data: {
      phone?: string | null;
      wechat_id?: string | null;
      mailing_address?: string | null;
      willing_song_library?: boolean;
      willing_artist_pool?: boolean;
      willing_activity_notifications?: boolean;
    }) => {
      return request('/creators/business-contact', {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // 提交音乐人档案进行审核
    submitProfile: async (profileData: any) => {
      return request('/creators/submit', {
        method: 'POST',
        body: JSON.stringify(profileData),
      });
    },

    // 获取音乐人档案状态
    getStatus: async () => {
      return request('/creators/status');
    },

    // 获取创作者收到的消息
    getMessages: async (page = 1, limit = 20) => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      return request(`/creators/messages?${params}`);
    },

    // 标记消息为已读
    markMessageAsRead: async (messageId: string) => {
      return request(`/creators/messages/${messageId}/read`, {
        method: 'PUT',
      });
    },

    // 获取未读消息数量
    getUnreadCount: async () => {
      return request('/creators/messages/unread-count');
    },

    // 获取收款信息
    getPaymentAccount: async () => {
      return request('/creators/payment-account');
    },

    // 保存/更新收款信息
    savePaymentAccount: async (data: {
      bank?: {
        accountName: string;
        accountNumber: string;
        bankName: string;
        bankBranch?: string;
        isPrimary?: boolean;
      };
      alipay?: {
        accountName: string;
        accountNumber: string;
        isPrimary?: boolean;
      };
    }) => {
      return request('/creators/payment-account', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // 检查收款信息是否已填写
    checkPaymentAccount: async () => {
      return request('/creators/payment-account/check');
    },

    // 获取创作者数据统计摘要（用于首页仪表板）
    getDashboardSummary: async () => {
      return request('/creators/dashboard-summary');
    },

    // 平台配置相关
    platforms: {
      // 获取平台配置（默认仅启用；includeInactive 供数据统计 logo 等使用）
      getAll: async (params?: {
        type?: 'music' | 'social';
        region?: 'china' | 'overseas';
        includeInactive?: boolean;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.type) queryParams.append('type', params.type);
        if (params?.region) queryParams.append('region', params.region);
        if (params?.includeInactive) queryParams.append('includeInactive', '1');
        const queryString = queryParams.toString();
        return request(`/creators/platforms${queryString ? `?${queryString}` : ''}`);
      },
    },
  },

  // 管理员相关
  admin: {
    // 获取管理后台统计数据
    getStats: async () => {
      return request('/admin/stats');
    },

    // 获取管理员首页数据汇总
    getDashboardSummary: async () => {
      return request('/admin/dashboard-summary');
    },

    // 获取管理员首页最近动态
    getRecentActivities: async () => {
      return request('/admin/dashboard/recent-activities');
    },

    // 获取用户登录日志（分页，可选 channel: 'web'|'wechat', success: 0|1）
    getLoginLogs: async (page = 1, limit = 20, params?: { channel?: string; success?: number }) => {
      const search = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (params?.channel) search.set('channel', params.channel);
      if (params?.success !== undefined) search.set('success', String(params.success));
      return request(`/admin/login-logs?${search}`);
    },

    getCollaboratorInvites: async (
      page = 1,
      limit = 20,
      params?: { status?: string; q?: string; days?: number }
    ) => {
      const search = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (params?.status) search.set('status', params.status);
      if (params?.q) search.set('q', params.q);
      if (params?.days != null) search.set('days', String(params.days));
      return request(`/admin/collaborator-invites?${search}`);
    },

    getEmailLogs: async (
      page = 1,
      limit = 20,
      params?: {
        email_type?: string;
        status?: string;
        deliver_status?: string;
        q?: string;
        days?: number;
      }
    ) => {
      const search = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (params?.email_type) search.set('email_type', params.email_type);
      if (params?.status) search.set('status', params.status);
      if (params?.deliver_status) search.set('deliver_status', params.deliver_status);
      if (params?.q) search.set('q', params.q);
      if (params?.days != null) search.set('days', String(params.days));
      return request(`/admin/email-logs?${search}`);
    },

    getEmailLogPreview: async (logId: string) => {
      return request(`/admin/email-logs/${encodeURIComponent(logId)}/preview`);
    },

    // 获取近期在线用户（心跳 last_seen_at）
    getOnlineUsers: async (params?: { window?: number; channel?: string; role?: string }) => {
      const search = new URLSearchParams();
      if (params?.window != null) search.set('window', String(params.window));
      if (params?.channel) search.set('channel', params.channel);
      if (params?.role) search.set('role', params.role);
      const qs = search.toString();
      return request(`/admin/online-users${qs ? `?${qs}` : ''}`);
    },

    staff: {
      list: async () => request('/admin/staff'),
      catalog: async () => request('/admin/staff/catalog'),
      setPermissions: async (userId: string, permissions: string[]) =>
        request(`/admin/staff/${encodeURIComponent(userId)}/permissions`, {
          method: 'PUT',
          body: JSON.stringify({ permissions }),
        }),
      promote: async (payload: { email?: string; userId?: string }) =>
        request('/admin/staff/promote', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
      demote: async (userId: string) =>
        request(`/admin/staff/${encodeURIComponent(userId)}/demote`, {
          method: 'POST',
        }),
    },

    // 获取待审核创作者列表
    getPendingCreators: async (page = 1, limit = 20, search = '') => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
      });
      return request(`/admin/creators/pending?${params}`);
    },

    // 获取创作者详情
    getCreator: async (creatorId: string) => {
      return request(`/admin/creators/${creatorId}`);
    },

    // 审核音乐人档案
    reviewCreator: async (
      creatorId: string,
      status: string,
      notes?: string,
      rejectionReasons?: string,
      messageContent?: string,
      sendEmail?: boolean
    ) => {
      return request(`/admin/creators/${creatorId}/review`, {
        method: 'PUT',
        body: JSON.stringify({
          verification_status: status,
          verification_notes: notes,
          rejection_reasons: rejectionReasons,
          message_content: messageContent,
          send_email: sendEmail === true
        }),
      });
    },

    // 获取所有创作者列表
    getCreators: async (
      page = 1,
      limit = 20,
      search = '',
      status = '',
      willingSongLibrary?: boolean,
      willingArtistPool?: boolean,
      willingActivityNotifications?: boolean
    ) => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
        status: status,
      });
      if (willingSongLibrary) params.set('willing_song_library', '1');
      if (willingArtistPool) params.set('willing_artist_pool', '1');
      if (willingActivityNotifications) params.set('willing_activity_notifications', '1');
      return request(`/admin/creators?${params}`);
    },

    // 更新创作者商务合作与联系方式（管理员）
    updateCreatorBusinessContact: async (
      creatorId: string,
      data: {
        phone?: string | null;
        wechat_id?: string | null;
        mailing_address?: string | null;
        willing_song_library?: boolean;
        willing_artist_pool?: boolean;
        willing_activity_notifications?: boolean;
      }
    ) => {
      return request(`/admin/creators/${creatorId}/business-contact`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    updateCreatorProfileCorrection: async (
      creatorId: string,
      data: {
        display?: { artist_name?: string; bio?: string };
        identity?: {
          real_name?: string;
          id_card?: string;
          identity_country?: string;
          identity_document_type?: string;
          identity_issuing_country?: string | null;
        };
        reason?: string;
        notify?: boolean;
        message_content?: string;
        send_email?: boolean;
      }
    ) => {
      return request(`/admin/creators/${creatorId}/profile-correction`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // 获取音乐人数据统计摘要列表
    getCreatorsDataSummary: async (page = 1, limit = 20, search = '') => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
      });
      return request(`/admin/creators/data-summary?${params}`);
    },

    // 获取用户列表
    getUsers: async (page = 1, limit = 20, search = '') => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
      });
      return request(`/admin/users?${params}`);
    },

    // 发送消息给创作者
    sendMessage: async (userId: string, messageData: { type: string; title: string; content: string; send_email?: boolean }) => {
      return request(`/admin/messages/${userId}`, {
        method: 'POST',
        body: JSON.stringify(messageData),
      });
    },

    // 更新作品审核状态
    updateWorkStatus: async (
      id: string,
      status: 'pending' | 'approved' | 'distributing' | 'rejected' | 'released',
      reason?: string,
      message?: string,
      sendNotification?: boolean,
      sendEmail?: boolean
    ) => {
      const body: {
        status: typeof status;
        reason?: string;
        message?: string;
        send_notification?: boolean;
        send_email?: boolean;
      } = { status };
      if (reason != null && reason !== '') body.reason = reason;
      if (message != null && message !== '') body.message = message;
      if (sendNotification === false) body.send_notification = false;
      if (sendEmail === true) body.send_email = true;
      return request(`/admin/works/works/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
    },

    // AI经纪人相关
    aiBroker: {
      // 获取用户使用情况列表
      getUsers: async (page = 1, limit = 20, search = '') => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          search: search,
        });
        return request(`/admin/ai-broker/users?${params}`);
      },
      // 获取用户详情（包含会话列表）
      getUser: async (userId: string, page = 1, limit = 20) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        return request(`/admin/ai-broker/users/${userId}?${params}`);
      },
      // 获取对话详情（管理员可查看任意对话）
      getConversation: async (conversationId: string) => {
        return request(`/admin/ai-broker/conversations/${conversationId}`);
      },
      // 获取所有对话列表（管理员）
      getConversations: async (page = 1, limit = 20, search = '', dateRange = '') => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          search: search,
          dateRange: dateRange,
        });
        return request(`/admin/ai-broker/conversations?${params}`);
      },
      // 获取整体统计数据
      getStats: async () => {
        return request('/admin/ai-broker/stats');
      },
    },

    // 获取管理员发送的消息历史
    getSentMessages: async (page = 1, limit = 20) => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      return request(`/admin/messages/sent?${params}`);
    },

    // 获取创作者管理员操作历史
    getCreatorAuditHistory: async (creatorId: string) => {
      return request(`/admin/creators/${creatorId}/audit-history`);
    },

    // 获取作品管理员操作历史
    getWorkAuditHistory: async (albumId: string) => {
      return request(`/admin/works/works/${albumId}/audit-history`);
    },

    // 用户通知设置相关
    notificationPreferences: {
      // 获取所有用户的通知设置列表
      getList: async (page = 1, limit = 20, search = '') => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          search: search,
        });
        return request(`/admin/users/notification-preferences?${params}`);
      },
      // 获取单个用户的完整通知设置
      getUserDetail: async (userId: string) => {
        return request(`/admin/users/${userId}/notification-preferences`);
      },
    },

    // 上传平台链接
    uploadPlatformLink: async (creatorId: string, platformId: string, platformUrl: string) => {
      return request(`/admin/creators/${creatorId}/platforms/${platformId}/link`, {
        method: 'PUT',
        body: JSON.stringify({
          platform_url: platformUrl
        }),
      });
    },

    /**
     * 添加/补全平台链接（可无 URL，仅传 hidden / live_display_without_url；与后端 POST 一致）
     */
    addPlatformLink: async (
      creatorId: string,
      platform: string,
      payload: {
        platform_url?: string | null;
        hidden?: boolean;
        live_display_without_url?: boolean;
        message_content?: string;
        send_notification?: boolean;
        send_email?: boolean;
      }
    ) => {
      const body: Record<string, unknown> = { platform };
      if (payload.platform_url !== undefined) {
        const u = payload.platform_url;
        body.platform_url = u === null || u === '' ? '' : String(u).trim();
      }
      if (payload.hidden !== undefined) body.hidden = payload.hidden;
      if (payload.live_display_without_url !== undefined) body.live_display_without_url = payload.live_display_without_url;
      if (payload.send_notification === false) {
        body.send_notification = false;
      } else if (payload.message_content != null && payload.message_content.trim() !== '') {
        body.message_content = payload.message_content.trim();
      }
      if (payload.send_email === true) body.send_email = true;
      return request(`/admin/creators/${creatorId}/platforms`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /** 更新单条艺人音乐平台链接（URL、对外展示、无链曲库点亮；首次填写 URL 时可传站内信） */
    updateCreatorPlatformLink: async (
      creatorId: string,
      linkId: string,
      body: {
        platform_url?: string | null;
        hidden?: boolean;
        live_display_without_url?: boolean;
        message_content?: string;
        send_notification?: boolean;
        send_email?: boolean;
      }
    ) => {
      return request(`/admin/creators/${creatorId}/platform-links/${linkId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    // 收益管理相关
    revenue: {
      // 获取分成比例列表
      getShareRatios: async (options: { search?: string; page?: number; limit?: number } = {}) => {
        const params = new URLSearchParams();
        if (options.search) params.append('search', options.search);
        if (options.page) params.append('page', options.page.toString());
        if (options.limit) params.append('limit', options.limit.toString());
        const queryString = params.toString();
        return request(`/admin/revenue/share-ratios${queryString ? `?${queryString}` : ''}`);
      },

      // 获取指定创作者的分成比例
      getShareRatio: async (creatorId: string) => {
        return request(`/admin/revenue/share-ratios/${creatorId}`);
      },

      // 设置/修改分成比例
      setShareRatio: async (creatorId: string, shareRatio: number, effectiveFrom?: string) => {
        return request(`/admin/revenue/share-ratios/${creatorId}`, {
          method: 'PUT',
          body: JSON.stringify({ 
            share_ratio: shareRatio,
            ...(effectiveFrom && { effective_from: effectiveFrom })
          }),
        });
      },

      // 获取分成比例历史记录
      getShareRatioHistory: async (creatorId: string) => {
        return request(`/admin/revenue/share-ratios/${creatorId}/history`);
      },


      // 获取创作者收益摘要列表
      getCreatorsRevenueSummary: async (page = 1, limit = 20, search = '') => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          search: search,
        });
        return request(`/admin/revenue/creators-summary?${params}`);
      },

      // 获取创作者收益分配状态概览
      getCreatorsAllocationStatus: async (options: {
        page?: number;
        limit?: number;
        search?: string;
      } = {}) => {
        const params = new URLSearchParams();
        if (options.page) params.append('page', options.page.toString());
        if (options.limit) params.append('limit', options.limit.toString());
        if (options.search) params.append('search', options.search);
        return request(`/admin/revenue/creators-allocation-status?${params}`);
      },

      // 获取收益分配预览（实时查询）
      getAllocationPreview: async (options: {
        creator_id?: string;
        month?: string;
        market?: 'china' | 'overseas';
        search?: string;
        page?: number;
        limit?: number;
      } = {}) => {
        const params = new URLSearchParams();
        if (options.creator_id) params.append('creator_id', options.creator_id);
        if (options.month) params.append('month', options.month);
        if (options.market) params.append('market', options.market);
        if (options.search) params.append('search', options.search);
        if (options.page) params.append('page', options.page.toString());
        if (options.limit) params.append('limit', options.limit.toString());
        const queryString = params.toString();
        return request(`/admin/revenue/allocation-preview${queryString ? `?${queryString}` : ''}`);
      },

      // 获取所有创作者的提现记录列表
      getWithdrawals: async (options: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
        market?: string;
      } = {}) => {
        const params = new URLSearchParams();
        if (options.page) params.append('page', options.page.toString());
        if (options.limit) params.append('limit', options.limit.toString());
        if (options.search) params.append('search', options.search);
        if (options.status) params.append('status', options.status);
        if (options.market) params.append('market', options.market);
        const queryString = params.toString();
        return request(`/admin/revenue/withdrawals${queryString ? `?${queryString}` : ''}`);
      },

      // 获取提现详情
      getWithdrawalDetail: async (withdrawalId: string) => {
        return request(`/admin/revenue/withdrawals/${withdrawalId}`);
      },

      // 完成提现转账
      completeWithdrawal: async (withdrawalId: string, data: {
        transactionReference: string;
        adminNote?: string;
        send_email?: boolean;
      }) => {
        return request(`/admin/revenue/withdrawals/${withdrawalId}/complete`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
      },

      // 获取提现操作历史
      getWithdrawalHistory: async (withdrawalId: string) => {
        return request(`/admin/revenue/withdrawals/${withdrawalId}/history`);
      },

      // 个税扣缴申报
      getTaxFilings: async (options: {
        page?: number;
        limit?: number;
        search?: string;
        tax_period?: string;
        status?: string;
      } = {}) => {
        const params = new URLSearchParams();
        if (options.page) params.append('page', options.page.toString());
        if (options.limit) params.append('limit', options.limit.toString());
        if (options.search) params.append('search', options.search);
        if (options.tax_period) params.append('tax_period', options.tax_period);
        if (options.status) params.append('status', options.status);
        const queryString = params.toString();
        return request(`/admin/revenue/tax-filings${queryString ? `?${queryString}` : ''}`);
      },

      createTaxFiling: async (data: {
        taxPeriod: string;
        creatorId: string;
        incomeAmount: number;
        taxAmount?: number;
        incomeItem?: string;
        status?: 'draft' | 'completed';
        withdrawalId?: string | null;
      }) => {
        return request('/admin/revenue/tax-filings', {
          method: 'POST',
          body: JSON.stringify(data),
        });
      },

      updateTaxFiling: async (
        filingId: string,
        data: {
          taxPeriod?: string;
          incomeAmount?: number;
          taxAmount?: number;
          incomeItem?: string;
          status?: 'draft' | 'completed';
          withdrawalId?: string | null;
        }
      ) => {
        return request(`/admin/revenue/tax-filings/${filingId}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
      },

      notifyTaxFiling: async (
        filingId: string,
        data: { title: string; content: string; send_email?: boolean }
      ) => {
        return request(`/admin/revenue/tax-filings/${filingId}/notify`, {
          method: 'POST',
          body: JSON.stringify(data),
        });
      },

      // 批量确认收益分配
      confirmAllocations: async (data: {
        creator_id: string;
        month: string;
        market: 'china' | 'overseas';
      }) => {
        return request('/admin/revenue/confirm-allocations', {
          method: 'POST',
          body: JSON.stringify(data),
        });
      },

      // 获取已确认的收益分配记录
      getConfirmedAllocations: async (options: {
        creator_id?: string;
        month?: string;
        market?: 'china' | 'overseas';
        search?: string;
        page?: number;
        limit?: number;
      } = {}) => {
        const params = new URLSearchParams();
        if (options.creator_id) params.append('creator_id', options.creator_id);
        if (options.month) params.append('month', options.month);
        if (options.market) params.append('market', options.market);
        if (options.search) params.append('search', options.search);
        if (options.page) params.append('page', options.page.toString());
        if (options.limit) params.append('limit', options.limit.toString());
        const queryString = params.toString();
        return request(`/admin/revenue/confirmed-allocations${queryString ? `?${queryString}` : ''}`);
      },
    },

    // 系统配置相关
    system: {
      feedback: {
        getList: async (params?: { page?: number; limit?: number; status?: string; category?: string; search?: string }) => {
          const queryParams = new URLSearchParams();
          if (params?.page != null) queryParams.append('page', String(params.page));
          if (params?.limit != null) queryParams.append('limit', String(params.limit));
          if (params?.status) queryParams.append('status', params.status);
          if (params?.category) queryParams.append('category', params.category);
          if (params?.search) queryParams.append('search', params.search);
          const query = queryParams.toString();
          return request(`/feedback/admin/list${query ? `?${query}` : ''}`);
        },
        updateStatus: async (id: string, status: 'pending' | 'processing' | 'resolved') => {
          return request(`/feedback/admin/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
          });
        },
      },
      helpCenter: {
        getCategories: async () => {
          return request('/help-center/admin/categories');
        },
        createCategory: async (data: {
          name: string;
          slug?: string;
          description?: string | null;
          sort_order?: number;
          is_active?: boolean;
        }) => {
          return request('/help-center/admin/categories', {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        updateCategory: async (
          id: string,
          data: {
            name: string;
            slug?: string;
            description?: string | null;
            sort_order?: number;
            is_active?: boolean;
          }
        ) => {
          return request(`/help-center/admin/categories/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          });
        },
        deleteCategory: async (id: string) => {
          return request(`/help-center/admin/categories/${id}`, {
            method: 'DELETE',
          });
        },
        getArticles: async (params?: { status?: 'draft' | 'published' }) => {
          const query = new URLSearchParams();
          if (params?.status) query.set('status', params.status);
          return request(`/help-center/admin/articles${query.toString() ? `?${query.toString()}` : ''}`);
        },
        getStats: async () => {
          return request('/help-center/admin/stats');
        },
        createArticle: async (data: {
          category_id?: string | null;
          title: string;
          summary?: string | null;
          content_html: string;
          cover_image_url?: string | null;
          tags?: string[];
          target_roles?: string[];
          status?: 'draft' | 'published';
          sort_order?: number;
          is_featured?: boolean;
        }) => {
          return request('/help-center/admin/articles', {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        updateArticle: async (
          id: string,
          data: {
            category_id?: string | null;
            title: string;
            summary?: string | null;
            content_html: string;
            cover_image_url?: string | null;
            tags?: string[];
            target_roles?: string[];
            status?: 'draft' | 'published';
            sort_order?: number;
            is_featured?: boolean;
          }
        ) => {
          return request(`/help-center/admin/articles/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          });
        },
        deleteArticle: async (id: string) => {
          return request(`/help-center/admin/articles/${id}`, {
            method: 'DELETE',
          });
        },
      },
      // 平台管理
      platforms: {
        // 获取所有平台配置
        getAll: async (params?: { type?: 'music' | 'social'; active?: boolean; region?: 'china' | 'overseas' }) => {
          const queryParams = new URLSearchParams();
          if (params?.type) queryParams.append('type', params.type);
          if (params?.active !== undefined) queryParams.append('active', params.active.toString());
          if (params?.region) queryParams.append('region', params.region);
          const queryString = queryParams.toString();
          return request(`/admin/platforms${queryString ? `?${queryString}` : ''}`);
        },
        // 获取单个平台配置
        get: async (id: string) => {
          return request(`/admin/platforms/${id}`);
        },
        // 创建平台配置
        create: async (data: {
          platform_id: string;
          platform_name: string;
          platform_type: 'music' | 'social';
          region?: 'china' | 'overseas';
          placeholder?: string;
          sort_order?: number;
        }) => {
          return request('/admin/platforms', {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        // 更新平台配置
        update: async (id: string, data: {
          platform_name?: string;
          platform_type?: 'music' | 'social';
          region?: 'china' | 'overseas';
          placeholder?: string;
          is_active?: boolean;
          sort_order?: number;
        }) => {
          return request(`/admin/platforms/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          });
        },
        // 更新平台Logo URL
        updateLogo: async (id: string, logoUrl: string) => {
          return request(`/admin/platforms/${id}/logo`, {
            method: 'PUT',
            body: JSON.stringify({ logo_url: logoUrl }),
          });
        },
        // 批量更新排序
        updateSort: async (platforms: Array<{ id: string; sort_order: number }>) => {
          return request('/admin/platforms/sort', {
            method: 'PUT',
            body: JSON.stringify({ platforms }),
          });
        },
        // 删除平台（软删除：禁用）
        delete: async (id: string) => {
          return request(`/admin/platforms/${id}`, {
            method: 'DELETE',
          });
        },
        // 上传Logo
        uploadLogo: async (platformId: string, file: File) => {
          return uploadFile(file, `admin/platforms/${platformId}/logo`, { skipValidation: true });
        },
      },
      // 获取汇率配置
      getExchangeRate: async () => {
        return request('/admin/system/exchange-rate');
      },

      // 更新汇率配置
      updateExchangeRate: async (rate: number) => {
        return request('/admin/system/exchange-rate', {
          method: 'PUT',
          body: JSON.stringify({ rate }),
        });
      },

      // 获取迷你经纪可见性配置（管理员）
      getMiniBrokerVisibility: async () => {
        return request('/admin/system/mini-broker-visibility');
      },

      // 更新迷你经纪可见性配置（管理员）
      updateMiniBrokerVisibility: async (visible: boolean) => {
        return request('/admin/system/mini-broker-visibility', {
          method: 'PUT',
          body: JSON.stringify({ visible }),
        });
      },

      // 获取版权登记价格配置（管理员）
      getCopyrightPrice: async () => {
        return request('/admin/system/copyright-price');
      },

      // 更新版权登记价格配置（管理员）：双单价，或传单个 price 兼容旧调用
      updateCopyrightPrice: async (payload: { ncacPerRegistration: number; mcscPerSong: number } | number) => {
        const body =
          typeof payload === 'number' ? { price: payload } : payload;
        return request('/admin/system/copyright-price', {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      },

      // 获取优化音频保留单价配置（管理员）
      getOptimizationKeepPrice: async () => {
        return request('/admin/system/optimization-keep-price');
      },

      // 更新优化音频保留单价配置（管理员）
      updateOptimizationKeepPrice: async (price: number) => {
        return request('/admin/system/optimization-keep-price', {
          method: 'PUT',
          body: JSON.stringify({ price }),
        });
      },

      // 获取微信号配置（管理员）
      getWechatConfig: async (key: string) => {
        return request(`/admin/system/wechat-config/${key}`);
      },

      // 更新微信号配置（管理员）
      updateWechatConfig: async (key: string, value: string) => {
        return request(`/admin/system/wechat-config/${key}`, {
          method: 'PUT',
          body: JSON.stringify({ value }),
        });
      },

      // 协议版本管理（管理员）
      agreements: {
        // 获取协议类型列表
        getTypes: async () => {
          return request('/admin/system/agreement-types');
        },
        // 获取所有协议版本列表
        getAll: async (params?: { agreement_type?: string }) => {
          const queryParams = new URLSearchParams();
          if (params?.agreement_type) queryParams.append('agreement_type', params.agreement_type);
          const queryString = queryParams.toString();
          return request(`/admin/system/agreements${queryString ? `?${queryString}` : ''}`);
        },
        // 获取单个协议版本详情
        get: async (id: string) => {
          return request(`/admin/system/agreements/${id}`);
        },
        // 创建协议版本
        create: async (data: {
          agreement_type: string;
          version: string;
          title: string;
          content: string;
          effective_date?: string;
          is_active?: boolean;
        }) => {
          return request('/admin/system/agreements', {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        // 更新协议版本
        update: async (id: string, data: {
          title?: string;
          content?: string;
          effective_date?: string;
          is_active?: boolean;
        }) => {
          return request(`/admin/system/agreements/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          });
        },
        // 删除协议版本
        delete: async (id: string) => {
          return request(`/admin/system/agreements/${id}`, {
            method: 'DELETE',
          });
        },
      },

      // 订阅套餐管理（管理员）
      subscriptionPlans: {
        // 获取所有套餐（包括停用的）
        getAll: async () => {
          return request('/admin/subscription-plans');
        },
        // 获取单个套餐详情
        get: async (id: string) => {
          return request(`/admin/subscription-plans/${id}`);
        },
        // 创建新套餐
        create: async (data: {
          plan_key: string;
          name: string;
          description: string;
          price: number;
          album_quota: number;
          features: any;
          is_active?: boolean;
          display_order?: number;
        }) => {
          return request('/admin/subscription-plans', {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        // 更新套餐
        update: async (id: string, data: {
          name?: string;
          description?: string;
          price?: number;
          album_quota?: number;
          features?: any;
          is_active?: boolean;
          display_order?: number;
        }) => {
          return request(`/admin/subscription-plans/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          });
        },
        // 删除套餐（软删除）
        delete: async (id: string) => {
          return request(`/admin/subscription-plans/${id}`, {
            method: 'DELETE',
          });
        },
        // 批量调整排序
        reorder: async (planIds: string[]) => {
          return request('/admin/subscription-plans/reorder', {
            method: 'PUT',
            body: JSON.stringify({ planIds }),
          });
        },
        // 获取套餐使用情况
        getUsage: async (id: string) => {
          return request(`/admin/subscription-plans/${id}/usage`);
        },
      },
      // 订阅兑换码管理（管理员）
      userSubscriptions: {
        getList: async (params?: {
          page?: number;
          limit?: number;
          /** @deprecated 请用 entitlement */
          status?: string;
          entitlement?: '' | 'in_use' | 'ended' | 'terminated' | 'superseded';
          plan_key?: string;
          search?: string;
          quota_alert?: 'album_zero' | 'copyright_zero' | '';
          source_type?: '' | 'redemption' | 'self_paid';
          list_mode?: 'canonical' | 'all';
          /** 全部记录时：1=含顶替行，默认 0 隐藏 */
          include_superseded?: '0' | '1';
        }) => {
          const queryParams = new URLSearchParams();
          if (params?.page != null) queryParams.append('page', params.page.toString());
          if (params?.limit != null) queryParams.append('limit', params.limit.toString());
          if (params?.entitlement) queryParams.append('entitlement', params.entitlement);
          else if (params?.status) queryParams.append('status', params.status);
          if (params?.plan_key) queryParams.append('plan_key', params.plan_key);
          if (params?.search) queryParams.append('search', params.search);
          if (params?.quota_alert) queryParams.append('quota_alert', params.quota_alert);
          if (params?.source_type) queryParams.append('source_type', params.source_type);
          if (params?.list_mode) queryParams.append('list_mode', params.list_mode);
          if (params?.include_superseded) queryParams.append('include_superseded', params.include_superseded);
          const q = queryParams.toString();
          return request(`/admin/user-subscriptions${q ? `?${q}` : ''}`);
        },
        terminate: async (subscriptionId: string, data: { title?: string; content: string; send_email?: boolean }) => {
          return request(`/admin/user-subscriptions/${subscriptionId}/terminate`, {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        grantAlbumQuota: async (
          subscriptionId: string,
          data: {
            amount: number;
            reason: string;
            notify?: boolean;
            title?: string;
            content?: string;
            send_email?: boolean;
          }
        ) => {
          return request(`/admin/user-subscriptions/${subscriptionId}/grant-album-quota`, {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        getCreatorPreview: async (userId: string) => {
          return request(`/admin/users/${userId}/subscription-preview`);
        },
      },
      orders: {
        getList: async (params?: { page?: number; limit?: number; status?: string; order_type?: string; search?: string }) => {
          const queryParams = new URLSearchParams();
          if (params?.page != null) queryParams.append('page', params.page.toString());
          if (params?.limit != null) queryParams.append('limit', params.limit.toString());
          if (params?.status) queryParams.append('status', params.status);
          if (params?.order_type) queryParams.append('order_type', params.order_type);
          if (params?.search) queryParams.append('search', params.search);
          const q = queryParams.toString();
          return request(`/admin/orders${q ? `?${q}` : ''}`);
        },
      },
      redemptionCodes: {
        getAll: async (params?: { status?: string; campaign_name?: string; plan_key?: string; page?: number; limit?: number }) => {
          const queryParams = new URLSearchParams();
          if (params?.status) queryParams.append('status', params.status);
          if (params?.campaign_name) queryParams.append('campaign_name', params.campaign_name);
          if (params?.plan_key) queryParams.append('plan_key', params.plan_key);
          if (params?.page != null) queryParams.append('page', params.page.toString());
          if (params?.limit != null) queryParams.append('limit', params.limit.toString());
          const q = queryParams.toString();
          return request(`/admin/subscription-redemption-codes${q ? `?${q}` : ''}`);
        },
        create: async (data: { plan_key: string; expires_at?: string | null; max_uses?: number; campaign_name?: string | null; count?: number }) => {
          return request('/admin/subscription-redemption-codes', {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        revoke: async (id: string) => {
          return request(`/admin/subscription-redemption-codes/${id}/revoke`, {
            method: 'PUT',
          });
        },
      },
      channelPromotions: {
        getAll: async (params?: { page?: number; limit?: number }) => {
          const queryParams = new URLSearchParams();
          if (params?.page != null) queryParams.append('page', params.page.toString());
          if (params?.limit != null) queryParams.append('limit', params.limit.toString());
          const q = queryParams.toString();
          return request(`/admin/subscription-channel-promotions${q ? `?${q}` : ''}`);
        },
        get: async (id: string) => {
          return request(`/admin/subscription-channel-promotions/${id}`);
        },
        create: async (data: {
          channel_label?: string | null;
          discount_display_note?: string | null;
          code?: string | null;
          starts_at?: string | null;
          ends_at?: string | null;
          max_redemptions?: number | null;
          plan_discounts: { plan_key: string; discount_percent: number; discount_display_note?: string | null }[];
        }) => {
          return request('/admin/subscription-channel-promotions', {
            method: 'POST',
            body: JSON.stringify(data),
          });
        },
        update: async (
          id: string,
          data: {
            channel_label?: string | null;
            discount_display_note?: string | null;
            is_active?: boolean;
            starts_at?: string | null;
            ends_at?: string | null;
            max_redemptions?: number | null;
            plan_discounts?: { plan_key: string; discount_percent: number; discount_display_note?: string | null }[];
          }
        ) => {
          return request(`/admin/subscription-channel-promotions/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          });
        },
      },
      // 获取投资交易问卷列表
      getInvestmentSurveys: async (params?: { page?: number; limit?: number; search?: string; willing_to_sell?: string; album_id?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.search) queryParams.append('search', params.search);
        if (params?.willing_to_sell) queryParams.append('willing_to_sell', params.willing_to_sell);
        if (params?.album_id) queryParams.append('album_id', params.album_id);
        return request(`/admin/investment-surveys?${queryParams.toString()}`);
      },
      // 获取投资交易问卷详情
      getInvestmentSurveyById: async (surveyId: string) => {
        return request(`/admin/investment-surveys/${surveyId}`);
      },
    },
  },

  // 创作者收益
  revenue: {
    getCurrentShareRatio: async () => {
      return request('/revenue/share-ratio/current');
    }
  },

  // 反馈相关
  feedback: {
    // 提交反馈
    submit: async (feedbackData: { category: string; content: string }) => {
      return request('/feedback', {
        method: 'POST',
        body: JSON.stringify(feedbackData),
      });
    },
    // 获取用户反馈列表
    getList: async (page = 1, limit = 20) => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      return request(`/feedback?${params}`);
    },
  },

  // 帮助中心
  helpCenter: {
    getCategories: async () => {
      return request('/help-center/categories');
    },
    getArticles: async (params?: { category_id?: string; search?: string }) => {
      const query = new URLSearchParams();
      if (params?.category_id) query.set('category_id', params.category_id);
      if (params?.search) query.set('search', params.search);
      return request(`/help-center/articles${query.toString() ? `?${query.toString()}` : ''}`);
    },
    getArticle: async (id: string) => {
      return request(`/help-center/articles/${id}`);
    },
    trackView: async (id: string) => {
      return request(`/help-center/articles/${id}/view`, {
        method: 'POST',
      });
    },
    submitFeedback: async (id: string, isHelpful: boolean) => {
      return request(`/help-center/articles/${id}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ is_helpful: isHelpful }),
      });
    },
  },

  // 作品相关
  works: {
    // 获取作品列表
    getWorks: async (page = 1, limit = 10) => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      return request(`/works?${params}`);
    },

    // 获取作品详情
    getWork: async (id: string) => {
      return request(`/works/${id}`);
    },

    // 创建作品
    createWork: async (workData: any) => {
      return request('/works', {
        method: 'POST',
        body: JSON.stringify(workData),
      });
    },

    // 更新作品
    updateWork: async (id: string, workData: any) => {
      return request(`/works/${id}`, {
        method: 'PUT',
        body: JSON.stringify(workData),
      });
    },

    // 删除作品
    deleteWork: async (id: string) => {
      return request(`/works/${id}`, {
        method: 'DELETE',
      });
    },

    // 获取优化试听会话（创作者）
    getOptimizationSession: async (albumId: string) => {
      return request(`/works/${albumId}/optimization-session`);
    },

    // 激活优化试听会话（服务端按专辑有效曲目数计算上架选择窗口时长并开始计时，防止加载阶段耗时）
    activateOptimizationSession: async (albumId: string, sessionId: string) => {
      return request(`/works/${albumId}/optimization-session/activate`, {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      });
    },

    // 保存优化版本选择（创作者）
    saveOptimizationSelections: async (
      albumId: string,
      payload: {
        sessionId: string;
        /** true 时写入 final_submitted_at；过程保存须显式 false */
        finalize?: boolean;
        selections: Array<{
          trackId: string;
          selectedVariantId: string | null;
          isOriginal: boolean;
        }>;
      }
    ) => {
      return request(`/works/${albumId}/optimization-session/select`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    // 获取已解锁优化版本下载链接（需鉴权）
    getOptimizationVariantDownloadUrl: async (albumId: string, variantId: string) => {
      return request(`/works/${albumId}/optimization/variants/${variantId}/download-url`);
    },

    // 批量获取已解锁优化版本下载链接（需鉴权）
    getOptimizationVariantsBulkDownloadUrls: async (albumId: string, variantIds: string[]) => {
      return request(`/works/${albumId}/optimization/variants/bulk-download-urls`, {
        method: 'POST',
        body: JSON.stringify({ variantIds }),
      });
    },

    // 上报优化页漏斗埋点（失败不阻塞主流程）
    trackOptimizationFunnelEvent: async (
      albumId: string,
      payload: {
        eventName: string;
        sessionId?: string;
        offerId?: string;
        trackId?: string;
        variantId?: string;
        experimentGroup?: 'A' | 'B';
        eventValue?: number;
        metadata?: Record<string, unknown>;
      }
    ) => {
      return request(`/works/${albumId}/optimization/funnel-event`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  },

  // 文件上传相关
  upload: {
    // 上传音频文件
    uploadAudio: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      return request('/upload/audio', {
        method: 'POST',
        headers: {}, // 让浏览器自动设置Content-Type
        body: formData,
      });
    },

    // 上传图片文件
    uploadImage: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      return request('/upload/image', {
        method: 'POST',
        headers: {}, // 让浏览器自动设置Content-Type
        body: formData,
      });
    },

    // 上传文档文件
    uploadDocument: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      return request('/upload/document', {
        method: 'POST',
        headers: {}, // 让浏览器自动设置Content-Type
        body: formData,
      });
    },

    // 获取文件列表
    getFiles: async () => {
      return request('/upload/files');
    },

    // 删除文件
    deleteFile: async (key: string) => {
      return request(`/upload/files/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      });
    },

    // 获取文件访问URL
    getFileUrl: async (key: string) => {
      return request(`/upload/files/${encodeURIComponent(key)}/url`);
    },
  },

  // 数据分析相关
  analytics: {
    // 获取支持的表类型列表
    getTableTypes: async () => {
      return request('/analytics/table-types');
    },

    // 获取批次列表
    getBatches: async (options: { limit?: number; offset?: number; search?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.offset) params.append('offset', options.offset.toString());
      if (options.search) params.append('search', options.search);
      
      return request(`/analytics/batches?${params.toString()}`);
    },

    // 获取批次详情
    getBatchDetail: async (batchId: string) => {
      return request(`/analytics/batches/${batchId}`);
    },

    // 删除批次
    deleteBatch: async (batchId: string) => {
      return request(`/analytics/batches/${batchId}`, {
        method: 'DELETE',
      });
    },

    // 审核批次数据
    reviewBatch: async (batchId: string, reviewStatus: 'approved' | 'rejected', reviewComment?: string) => {
      return request(`/analytics/batches/${batchId}/review`, {
        method: 'PUT',
        body: JSON.stringify({
          review_status: reviewStatus,
          review_comment: reviewComment
        }),
      });
    },

    // 撤销审核
    revokeBatchReview: async (batchId: string) => {
      return request(`/analytics/batches/${batchId}/review/revoke`, {
        method: 'PUT',
      });
    },

    // 验证Excel文件
    validateExcel: async (file: File, tableType: string = 'song-metrics') => {
      const formData = new FormData();
      formData.append('excelFile', file);
      formData.append('tableType', tableType);
      
      return request('/analytics/validate', {
        method: 'POST',
        headers: {}, // 让浏览器自动设置Content-Type
        body: formData,
      });
    },

    // 验证歌曲ID
    validateTracks: async (trackIds: string[]) => {
      return request('/analytics/validate-tracks', {
        method: 'POST',
        body: JSON.stringify({ trackIds }),
      });
    },

    // 导入Excel数据
    importExcel: async (file: File, tableType: string) => {
      const formData = new FormData();
      formData.append('excelFile', file);
      formData.append('tableType', tableType);
      
      return request('/analytics/import', {
        method: 'POST',
        headers: {}, // 让浏览器自动设置Content-Type
        body: formData,
      });
    },

    // 获取模板
    getTemplates: async () => {
      return request('/analytics/templates');
    },

    // 创作者端数据查询
    // 获取用户的歌曲列表
    getUserTracks: async (options: { creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/tracks${queryString ? `?${queryString}` : ''}`);
    },

    // 获取歌曲的听众人口特征数据
    getListenerDemographics: async (trackId: string, options: { platform?: string; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.platform) params.append('platform', options.platform);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/tracks/${trackId}/listener-demographics?${params.toString()}`);
    },

    // 获取歌曲的听众地域分布数据
    getListenerGeographics: async (trackId: string, options: { platform?: string; limit?: number; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.platform) params.append('platform', options.platform);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/tracks/${trackId}/listener-geographics?${params.toString()}`);
    },

    // 获取创作者的粉丝人口特征数据
    getFollowerDemographics: async (options: { platform?: string; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.platform) params.append('platform', options.platform);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/follower-demographics?${params.toString()}`);
    },

    // 获取创作者的粉丝地域分布数据
    getFollowerGeographics: async (options: { platform?: string; limit?: number; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.platform) params.append('platform', options.platform);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/follower-geographics?${params.toString()}`);
    },

    // 获取创作者的粉丝层级数据
    getFollowerTiers: async (options: { platform?: string; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.platform) params.append('platform', options.platform);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/follower-tiers?${params.toString()}`);
    },

    // 获取创作者的粉丝汇总数据（各平台粉丝数 + 月趋势）
    getFollowersSummary: async (options: { creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/followers-summary${queryString ? `?${queryString}` : ''}`);
    },

    // 获取歌曲的流量细分数据
    getStreamsBreakdowns: async (trackId: string, options: { platform?: string; monthStart?: string; monthEnd?: string; segmentType?: string; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.platform) params.append('platform', options.platform);
      if (options.monthStart) params.append('monthStart', options.monthStart);
      if (options.monthEnd) params.append('monthEnd', options.monthEnd);
      if (options.segmentType) params.append('segmentType', options.segmentType);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/tracks/${trackId}/streams-breakdowns?${params.toString()}`);
    },

    // 获取歌曲的指标数据
    getSongMetrics: async (trackId: string, options: { platform?: string | string[]; monthStart?: string; monthEnd?: string; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.platform) {
        const platforms = Array.isArray(options.platform) ? options.platform.join(',') : options.platform;
        params.append('platform', platforms);
      }
      if (options.monthStart) params.append('monthStart', options.monthStart);
      if (options.monthEnd) params.append('monthEnd', options.monthEnd);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/tracks/${trackId}/metrics?${params.toString()}`);
    },

    // 获取歌曲的视频传播累计快照数据
    getTrackVideoMetrics: async (
      trackId: string,
      options: { platform?: string | string[]; snapshotStart?: string; snapshotEnd?: string; creatorId?: string } = {}
    ) => {
      const params = new URLSearchParams();
      if (options.platform) {
        const platforms = Array.isArray(options.platform) ? options.platform.join(',') : options.platform;
        params.append('platform', platforms);
      }
      if (options.snapshotStart) params.append('snapshotStart', options.snapshotStart);
      if (options.snapshotEnd) params.append('snapshotEnd', options.snapshotEnd);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      return request(`/analytics/tracks/${trackId}/video-metrics?${params.toString()}`);
    },

    // 获取收入汇总数据（中国市场 + 海外市场）
    getRevenueSummary: async (options: { albumIds?: string[]; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.albumIds && options.albumIds.length > 0) {
        params.append('album_ids', options.albumIds.join(','));
      }
      if (options.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/revenue/summary${queryString ? `?${queryString}` : ''}`);
    },

    // 获取收入趋势数据（月度累计收入）
    getRevenueTrend: async (options: { albumIds?: string[]; market?: 'china' | 'overseas' | 'all'; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.albumIds && options.albumIds.length > 0) {
        params.append('album_ids', options.albumIds.join(','));
      }
      if (options.market) params.append('market', options.market);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/revenue/trend${queryString ? `?${queryString}` : ''}`);
    },

    // 获取收益明细数据（按月份分组）
    getRevenueDetails: async (options: { albumIds?: string[]; market?: 'china' | 'overseas' | 'all'; creatorId?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.albumIds && options.albumIds.length > 0) {
        params.append('album_ids', options.albumIds.join(','));
      }
      if (options.market && options.market !== 'all') params.append('market', options.market);
      if (options.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/revenue/details${queryString ? `?${queryString}` : ''}`);
    },

    // 提交提现申请
    submitWithdrawal: async (data: { market: 'china' | 'overseas'; amount: number }) => {
      return request('/analytics/revenue/withdrawals', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // 获取提现记录列表
    getWithdrawals: async (options?: { page?: number; limit?: number; creatorId?: string }) => {
      const params = new URLSearchParams();
      if (options?.page) params.append('page', options.page.toString());
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/revenue/withdrawals${queryString ? `?${queryString}` : ''}`);
    },

    // 获取已提现金额统计
    getWithdrawalSummary: async (options?: { creatorId?: string }) => {
      const params = new URLSearchParams();
      if (options?.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/revenue/withdrawals/summary${queryString ? `?${queryString}` : ''}`);
    },

    // 创作者个税申报记录（只读）
    getTaxFilings: async (options?: { creatorId?: string }) => {
      const params = new URLSearchParams();
      if (options?.creatorId) params.append('creator_id', options.creatorId);
      const queryString = params.toString();
      return request(`/analytics/revenue/tax-filings${queryString ? `?${queryString}` : ''}`);
    },

    // 获取汇率（供创作者端使用）
    getExchangeRate: async () => {
      return request('/analytics/exchange-rate');
    },

    // 获取版权登记价格（公开接口，供创作者端使用）
    getCopyrightPrice: async () => {
      return request('/analytics/copyright-price');
    },

    // 获取微信号配置（公开接口，供创作者端使用）
    getWechatConfig: async (key: string) => {
      return request(`/analytics/wechat-config/${key}`);
    },
  },

  copyrightRegistration: {
    listCases: async () => {
      return request('/copyright-registration/cases');
    },
    getCase: async (id: string) => {
      return request(`/copyright-registration/cases/${encodeURIComponent(id)}`);
    },
    listAdditionalProofs: async (caseId: string) => {
      return request(`/copyright-registration/add-proofs/${encodeURIComponent(caseId)}`);
    },
    addAdditionalProofs: async (
      caseId: string,
      proofs: Array<{ file_url: string; file_name: string; description?: string | null }>
    ) => {
      return request(`/copyright-registration/add-proofs/${encodeURIComponent(caseId)}`, {
        method: 'POST',
        body: JSON.stringify({ proofs }),
      });
    },
  },

  // AI经纪人相关
  aiBroker: {
    // 检查是否有 AI 经纪群权限（多经纪人功能）
    getGroupAccess: async () => request('/ai-broker/group-access'),
    // 获取当日额度（剩余次数与上限）
    getQuota: async () => request('/ai-broker/quota'),
    // 发送消息
    chat: async (
      message: string,
      conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
      conversationId?: string,
      options?: {
        context?: { source: string; [k: string]: unknown };
        brokerType?: string;
        entrySource?: string;
      }
    ) => {
      const body: Record<string, unknown> = {
        message,
        conversationHistory: conversationHistory || [],
        conversationId: conversationId || undefined
      };
      if (options?.context) body.context = options.context;
      if (options?.brokerType) body.brokerType = options.brokerType;
      if (options?.entrySource) body.entrySource = options.entrySource;
      return request('/ai-broker/chat', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },
    // 发送消息（流式版本）
    chatStream: async function* (
      message: string,
      conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
      conversationId?: string,
      options?: {
        context?: { source: string; [k: string]: unknown };
        brokerType?: string;
        entrySource?: string;
      }
    ) {
      // 直接访问后端服务器，绕过 Next.js 代理（因为 Next.js rewrite 不支持 SSE）
      const backendUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002';
      const url = `${backendUrl}/api/ai-broker/chat-stream`;
      
      // 使用 accessToken（与其他 API 保持一致）
      const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      
      const body: Record<string, unknown> = {
        message,
        conversationHistory: conversationHistory || [],
        conversationId: conversationId || undefined
      };
      if (options?.context) body.context = options.context;
      if (options?.brokerType) body.brokerType = options.brokerType;
      if (options?.entrySource) body.entrySource = options.entrySource;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: '请求失败' }));
        throw new Error(errorData.error || '请求失败');
      }

      if (!response.body) {
        throw new Error('响应体不可读');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      /** 从 buffer 解析完整 SSE 事件，返回未结束的尾部 */
      const drainSseBuffer = (input: string): { rest: string; parsed: Array<{ event: string; data: unknown }> } => {
        const blocks = input.split('\n\n');
        const rest = blocks.pop() || '';
        const parsed: Array<{ event: string; data: unknown }> = [];
        for (const eventBlock of blocks) {
          if (!eventBlock.trim()) continue;
          const lines = eventBlock.split('\n');
          let event = 'message';
          let dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) event = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr = line.slice(6);
          }
          if (!dataStr.trim() || dataStr === '[DONE]') continue;
          try {
            parsed.push({ event, data: JSON.parse(dataStr) });
          } catch {
            /* 跳过损坏块 */
          }
        }
        return { rest, parsed };
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }
          const { rest, parsed } = drainSseBuffer(buffer);
          buffer = rest;
          for (const item of parsed) {
            yield item;
          }
          if (done) break;
        }
        // 流结束时 flush decoder 并处理尾部（含最后的 done 事件）
        buffer += decoder.decode(undefined, { stream: false });
        const { parsed: tail } = drainSseBuffer(buffer ? `${buffer}\n\n` : '');
        for (const item of tail) {
          yield item;
        }
      } finally {
        reader.releaseLock();
      }
    },
    // 获取对话历史
    getHistory: async (page = 1, limit = 20) => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      return request(`/ai-broker/history?${params}`);
    },
    // 获取会话列表
    getConversations: async (page = 1, limit = 20, brokerType?: string) => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      if (brokerType) params.set('brokerType', brokerType);
      return request(`/ai-broker/conversations?${params}`);
    },
    // 获取会话详情
    getConversation: async (conversationId: string) => {
      return request(`/ai-broker/conversations/${conversationId}`);
    },
    // 删除会话
    deleteConversation: async (conversationId: string) => {
      return request(`/ai-broker/conversations/${conversationId}`, {
        method: 'DELETE'
      });
    },
    // 更新会话标题
    updateConversationTitle: async (conversationId: string, title: string) => {
      return request(`/ai-broker/conversations/${conversationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title })
      });
    },
    // 更新会话置顶状态
    updateConversationPinned: async (conversationId: string, isPinned: boolean) => {
      return request(`/ai-broker/conversations/${conversationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ isPinned })
      });
    },
  },
};

export default api; 